/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { TourKind } from '@/stores/uiStore';
import type { Side } from './geometry';

/**
 * One way of presenting a stop: the element to spotlight and the copy that describes it. A stop
 * lists these in order of preference, and the first one that is on screen wins, so the copy always
 * matches what is highlighted (the rail on a desktop, the menu button that opens it on a phone).
 */
export interface TourTarget {
  selector: string;
  /** i18n keys. */
  title: string;
  body: string;
  /**
   * Skip this target when it is taller than this share of the viewport (a phone stacks the three
   * search cards into one long column), so a smaller target further down the list is used.
   */
  maxHeight?: number;
}

interface TourStopDef {
  id: string;
  /** Preferred side of the target for the popover; it flips when that side has no room. */
  side: Side;
  targets: TourTarget[];
  /** Dropped first when a tour resolves to more than {@link MAX_STOPS} stops. */
  optional?: boolean;
}

interface TourDef {
  stops: TourStopDef[];
  /**
   * Stops that make this tour worth taking. When none of them is on screen (the explorer before a
   * vehicle is chosen, an admin page still loading) the page tour gives way to the general one.
   */
  key: string[];
}

/** A stop resolved against the current screen: exactly one target, which is visible right now. */
export interface TourStop {
  id: string;
  side: Side;
  selector: string;
  title: string;
  body: string;
}

export interface ResolvedTour {
  /** The tour actually shown, which is `app` when the page tour had nothing to point at. */
  kind: TourKind;
  stops: TourStop[];
}

/** Keeps every tour short enough to finish in about a minute. */
export const MAX_STOPS = 6;

const tgt = (selector: string, key: string, maxHeight?: number): TourTarget => ({
  selector,
  title: `tour.${key}.title`,
  body: `tour.${key}.body`,
  maxHeight,
});

/* Chrome stops shared by the page tours. */
const NAV: TourStopDef = {
  id: 'nav',
  side: 'right',
  targets: [tgt('[data-tour="rail"]', 'chrome.nav'), tgt('.tb-burger', 'chrome.navMenu')],
};
const VEHICLE: TourStopDef = {
  id: 'vehicle',
  side: 'bottom',
  targets: [tgt('[data-tour="vehicle-picker"]', 'chrome.vehicle')],
  optional: true,
};
const HELP: TourStopDef = {
  id: 'help',
  side: 'bottom',
  targets: [tgt('[data-tour="help"]', 'chrome.help')],
};
const FEEDBACK: TourStopDef = {
  id: 'feedback',
  side: 'top',
  targets: [tgt('[data-tour="feedback"]', 'chrome.feedback')],
  optional: true,
};

const TOURS: Record<TourKind, TourDef> = {
  home: {
    key: ['search'],
    stops: [
      {
        id: 'search',
        side: 'bottom',
        targets: [
          tgt('[data-tour="search"]', 'home.search', 0.6),
          tgt('[data-tour="search"] > :first-child', 'home.searchVin'),
        ],
      },
      {
        id: 'resume',
        side: 'top',
        targets: [
          tgt('[data-tour="continue"]', 'home.continue'),
          tgt('[data-tour="recent"]', 'home.recent'),
        ],
      },
      NAV,
      HELP,
      FEEDBACK,
    ],
  },
  explorer: {
    key: ['tabs'],
    stops: [
      { id: 'tabs', side: 'bottom', targets: [tgt('[data-tour="se-tabs"]', 'explorer.tabs')] },
      {
        id: 'list',
        side: 'right',
        targets: [
          tgt('[data-tour="se-selection"]', 'explorer.list'),
          // Below 900px (or once collapsed) the list folds away behind its "Show list" button.
          tgt('.se-panel-toggle--labeled', 'explorer.listToggle'),
        ],
      },
      {
        id: 'canvas',
        side: 'left',
        targets: [
          tgt('[data-tour="se-3d"]', 'explorer.model'),
          // A rendered diagram (its zoom dock is up), not one still loading or that failed.
          tgt('[data-tour="se-canvas"]:has(.svg-dock)', 'explorer.diagram'),
          // Nothing selected (or rendered) yet: the pane the diagram will fill.
          tgt('.se-wrap .spl-main', 'explorer.empty'),
        ],
      },
      { id: 'tkv', side: 'bottom', targets: [tgt('[data-tour="se-tkv"]', 'explorer.tkv')] },
      { id: 'view', side: 'bottom', targets: [tgt('[data-tour="se-viewmode"]', 'explorer.view')] },
      { id: 'mecl', side: 'bottom', targets: [tgt('[data-tour="se-mecl"]', 'explorer.mecl')] },
      VEHICLE,
    ],
  },
  analytics: {
    key: ['filters', 'sections'],
    stops: [
      {
        id: 'filters',
        side: 'bottom',
        targets: [tgt('[data-tour="an-filters"]', 'analytics.filters')],
      },
      {
        id: 'sections',
        side: 'bottom',
        targets: [tgt('.an-page [role="tablist"]', 'analytics.sections')],
      },
      {
        id: 'report',
        side: 'top',
        targets: [tgt('[data-tour="an-metrics"]', 'analytics.report')],
      },
    ],
  },
  admin: {
    key: ['sections'],
    stops: [
      { id: 'sections', side: 'right', targets: [tgt('[data-tour="admin-nav"]', 'admin.nav')] },
      HELP,
    ],
  },
  app: {
    key: [],
    stops: [NAV, VEHICLE, HELP, FEEDBACK],
  },
};

/** Page tours a first visit is invited to; the general `app` tour is only ever asked for. */
export const INVITE_KINDS: readonly TourKind[] = ['home', 'explorer', 'analytics', 'admin'];

/** The tour that belongs to a route: the page's own one, or the general app tour. */
export function tourForPath(pathname: string): TourKind {
  if (pathname === '/') return 'home';
  const page = pathname.split('/')[1];
  if (page === 'schematic-explorer') return 'explorer';
  if (page === 'analytics') return 'analytics';
  if (page === 'admin') return 'admin';
  return 'app';
}

/**
 * Builds a tour from what is on screen now: each stop takes its first visible target and stops
 * with none are left out, so the walkthrough never points at something hidden and its "2 of 5"
 * count is the real one. A page tour whose key stops are all missing gives way to the app tour.
 */
export function resolveTour(
  kind: TourKind,
  findVisible: (selector: string) => Element | null,
): ResolvedTour {
  const def = TOURS[kind];
  const found: { stop: TourStop; optional: boolean }[] = [];
  const fits = ({ selector, maxHeight }: TourTarget) => {
    const el = findVisible(selector);
    return (
      !!el && (!maxHeight || el.getBoundingClientRect().height <= maxHeight * window.innerHeight)
    );
  };
  for (const { id, side, targets, optional = false } of def.stops) {
    const target = targets.find(fits);
    if (target) {
      const { selector, title, body } = target;
      found.push({ stop: { id, side, selector, title, body }, optional });
    }
  }
  if (def.key.length > 0 && !found.some(({ stop }) => def.key.includes(stop.id))) {
    return resolveTour('app', findVisible);
  }
  // Over the cap: drop optional stops, last first.
  let excess = found.length - MAX_STOPS;
  const kept = found
    .reverse()
    .filter(({ optional }) => !(optional && excess-- > 0))
    .reverse();
  return { kind, stops: kept.map(({ stop }) => stop) };
}
